import { requester } from '@/api/axios';

import { apiConst, keyConst } from '@/constants';

import {
  ApiResponse,
  ClientProfile,
  CreateMeetingSlotsResponse,
  CreateProjectForm,
  Expert,
  ExpertFormData,
  GenerateFilterForm,
  GetProjectInterface,
  Project,
  ProjectClient,
  ProjectMember,
  ProjectRole,
  UnscheduledMeetingSlot,
  UpdateProjectForm,
  createMeetingSlotsFrom,
  createRoleFrom,
  generateMeetingForm,
} from '@/interface';
import { INextProjectCodeResponse } from '@/interface/project.interface';

// import { IResolveProjectBySlugResponse, ISharedProjectLinkResponse } from '@/interface/IProjectSharedLink.interface';

// Generate Filter Request
export const generateFilters = async (data: GenerateFilterForm): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.generateFilters,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export interface SearchPeopleResponse {
  people: unknown[];
}

// Search People Request
export const searchPeoples = async (data: Record<string, unknown>): Promise<ApiResponse<SearchPeopleResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.searchPeoples,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};
export interface CreateProjectResponse {
  project: Project;
  filterSkipped?: boolean;
  message?: string;
}

export const getNextProjectCode = async (): Promise<ApiResponse<INextProjectCodeResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.projects}/next-code`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

// create Project Request
export const createProject = async (data: CreateProjectForm): Promise<ApiResponse<CreateProjectResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.projects,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// create and update Client Request
export const createAndPatchClient = async (
  projectId: string,
  data: { name: string[]; email: string[]; company?: string },
): Promise<ApiResponse<{ client: ProjectClient; projectId: string }>> => {
  const config = {
    method: 'post',
    url: `${apiConst.projects}/${projectId}/clients`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
export const deleteProjectClient = async (projectId: string): Promise<ApiResponse<{ projectId: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.projects}/${projectId}/clients`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export interface ClientProfilePayload {
  name: string[];
  email: string[];
  company?: string;
  parentClientId?: string;
}

export interface ParentClientProfileResponse {
  id: string;
  name: string;
  children?: ClientProfile[];
}

export const getClientProfiles = async (): Promise<ApiResponse<ClientProfile[]>> => {
  const config = {
    method: 'get',
    url: apiConst.clientInfo,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const createClientProfile = async (data: ClientProfilePayload): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.clientInfo,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const getParentClientProfiles = async (): Promise<ApiResponse<ParentClientProfileResponse[]>> => {
  const config = {
    method: 'get',
    url: `${apiConst.clientInfo}/parents`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};

export const createParentClientProfile = async (data: {
  name: string;
  children: { name: string[]; email: string[]; company?: string }[];
}): Promise<ApiResponse<ParentClientProfileResponse>> => {
  const config = {
    method: 'post',
    url: `${apiConst.clientInfo}/parents/with-children`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export const updateClientProfile = async (id: string, data: Partial<ClientProfilePayload>): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.clientInfo}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};
// update Project Request
export const updateProject = async (id: string, data: UpdateProjectForm): Promise<ApiResponse<Project>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.projects}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export interface ProjectEmailTemplatePayload {
  subject?: string;
  body?: string;
  isGlobalTemplate?: boolean;
}

export interface ProjectEmailTemplateResponse {
  canEditGlobal?: boolean;
  projectTitle?: string;
  subject: string;
  body: string;
  source?: 'project' | 'global' | 'empty';
  subjectSource?: 'project' | 'global' | 'empty';
  bodySource?: 'project' | 'global' | 'empty';
  project?: { subject: string; body: string };
  global?: { subject: string; body: string };
}

export const getProjectEmailTemplate = async (projectId: string): Promise<ApiResponse<ProjectEmailTemplateResponse>> => {
  const config = {
    method: 'get',
    url: `/projects/${projectId}/email-template`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  };

  return await requester(config);
};

export interface ProjectExpertsQuery {
  page?: number;
  limit?: number;
  status?: string;
}

export interface ProjectExpertSummary {
  projectExpertId?: string;
  [key: string]: unknown;
}

export interface ProjectExpertsResponse {
  projectId: string;
  experts: ProjectExpertSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages?: number;
}

export const getProjectExperts = async (
  projectId: string,
  query: ProjectExpertsQuery = {},
): Promise<ApiResponse<ProjectExpertsResponse>> => {
  const config = {
    method: 'post',
    url: `/projects/${projectId}/experts`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({
      page: query.page,
      limit: query.limit,
      ...(query.status && query.status !== 'all' ? { status: query.status } : {}),
    }),
  };

  return await requester(config);
};

export const updateProjectEmailTemplate = async (
  projectId: string,
  data: ProjectEmailTemplatePayload,
): Promise<ApiResponse<ProjectEmailTemplateResponse>> => {
  const config = {
    method: 'patch',
    url: `/projects/${projectId}/email-template`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// delete Project Request
export const deleteProject = async (id: string): Promise<ApiResponse<{ id: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.projects}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const deleteProjectExpert = async (id: string): Promise<ApiResponse<{ id: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.projectExpertDelete}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export const deleteProjectExpertsBulk = async (
  projectExpertIds: string[],
): Promise<ApiResponse<{ deletedIds?: string[]; notFoundIds?: string[] }>> => {
  const config = {
    method: 'delete',
    url: apiConst.projectExpertBulkDelete,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify({ projectExpertIds }),
  };

  return await requester(config);
};
// Add expert
export const addExpertToProject = async (data: ExpertFormData): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.experts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// update  expert
export const updateExpert = async (id: string, data: ExpertFormData): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.experts}/${id}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// update  project expert
export const updateProjectExpert = async (
  id: string,
  projectId: string | undefined,
  data: ExpertFormData,
): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.experts}/${id}/project` + `/${projectId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };
  return await requester(config);
};

export interface AddProjectMemberPayload {
  userId: string;
  projectId: string;
  projectRoleId: string;
}

export interface UpdateProjectMemberPayload {
  projectRoleId: string;
}

// Add member  Project Request
export const addMemberToProject = async (data: AddProjectMemberPayload): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.projectMembers,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
export const getSuggestedTenants = async (projectId: string, projectDetail?: Project) => {
  if (projectDetail && projectDetail.suggestedTenants) {
    return projectDetail.suggestedTenants;
  }

  const { data: projectDetailResponse } = await requester({
    method: 'get',
    url: `/projects/${projectId}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  });

  return projectDetailResponse.suggestedTenants || [];
};

export const getUnassignedProjects = async (): Promise<Project[]> => {
  const { data } = await requester({
    method: 'get',
    url: `/projects/unassigned`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  });

  return data || [];
};
export const assignProjectApi = async (projectId: string, companyId?: string): Promise<Project> => {
  const { data } = await requester({
    method: 'patch',
    url: `/projects/${projectId}/assign`,
    data: { companyId },
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  });

  return data;
};

export const unassignProjectApi = async (projectId: string): Promise<Project> => {
  const { data } = await requester({
    method: 'patch',
    url: `/projects/${projectId}/unassign`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  });

  return data;
};

// ud member  Project Request
export const updateMemberToProject = async (id: string, data: UpdateProjectMemberPayload): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: apiConst.updateprojectMembersRole + id,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
// get member  Project Request
export const getMembersOfProject = async (id: string): Promise<ApiResponse<ProjectMember[]>> => {
  const config = {
    method: 'get',
    url: `${apiConst.projectMembers}/byProject/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
// get member  Project Request
export const getApprovedExperts = async (id: string): Promise<ApiResponse<Expert[]>> => {
  const config = {
    method: 'get',
    url: `${apiConst.getApprovedExperts}${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
// delete member  Project Request
export const deleteMembersOfProject = async (id: string): Promise<ApiResponse<{ id: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.projectMembers}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};
export const getProjects = async (): Promise<ApiResponse<GetProjectInterface[]>> => {
  const config = {
    method: 'get',
    url: apiConst.getAllProjects,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

export type ProjectDetailResponse = Project & { name?: string; experts?: unknown; projectExperts?: unknown };

export const getProjectById = async (id: string): Promise<ApiResponse<ProjectDetailResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.projects}/${id}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  };

  return await requester(config);
};
export const getUnscheduledMeetings = async (
  id: string,
  date: string,
): Promise<ApiResponse<{ data: UnscheduledMeetingSlot[] }>> => {
  const config = {
    method: 'get',
    url: `${apiConst.unscheduledMeetingList + id}/${date}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
  };

  return await requester(config);
};
export const generateMeeting = async (id: string, data: generateMeetingForm): Promise<ApiResponse<{ authUrl?: string }>> => {
  const config = {
    method: 'patch',
    url: apiConst.generateMeeting + id,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};

export const getProjectCreatePermissions = async (): Promise<ApiResponse<string[]>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'get',
    url: apiConst.getProjectCreatePermissions,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  };

  return await requester(config);
};
export const getProjectRoles = async (): Promise<ApiResponse<ProjectRole[]>> => {
  const token = localStorage.getItem(keyConst.userToken);
  const config = {
    method: 'get',
    url: apiConst.getProjectRole,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  };

  return await requester(config);
};
export const createProjectRole = async (data: createRoleFrom): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'post',
    url: apiConst.createProjectRole,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
export const updateProjectRole = async (id: string, data: createRoleFrom): Promise<ApiResponse<unknown>> => {
  const config = {
    method: 'patch',
    url: `${apiConst.updateProjectRole}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
export const deleteProjectRole = async (id: string): Promise<ApiResponse<{ id?: string }>> => {
  const config = {
    method: 'delete',
    url: `${apiConst.deleteProjectRole}/${id}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
  };
  return await requester(config);
};
export const createMeetingSlots = async (data: createMeetingSlotsFrom): Promise<ApiResponse<CreateMeetingSlotsResponse>> => {
  const config = {
    method: 'post',
    url: apiConst.createSlots,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(data),
  };

  return await requester(config);
};
export const getSharedProjectById = async (id: string): Promise<ApiResponse<ProjectDetailResponse>> => {
  const config = {
    method: 'get',
    url: `${apiConst.projects}/shared/${id}`,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return await requester(config);
};

// export const getSharedProjectLink = async (projectId: string): Promise<ApiResponse<ISharedProjectLinkResponse>> => {
//   const config = {
//     method: 'get',
//     url: `${apiConst.getSharedProjectLink}/${projectId}/shared-project-link`,
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
//       'Content-Type': 'application/json',
//     },
//   };

//   return await requester(config);
// };

// export const resolveProjectBySlug = async (slug: string): Promise<ApiResponse<IResolveProjectBySlugResponse>> => {
//   const config = {
//     method: 'get',
//     url: `${apiConst.resolveProjectBySlug}/${slug}`,
//     headers: {
//       Authorization: `Bearer ${localStorage.getItem(keyConst.userToken)}`,
//       'Content-Type': 'application/json',
//     },
//   };

//   return await requester(config);
// };
